/**
 * WP6 / DUP-H2/H3: felles byggesteiner for faktureringsgrad-input.
 *
 * Tidligere var den samme inner-join-querien (assignments → budget_lines!inner
 * → projects!inner, completed/approved + soft-delete + cancelled) håndskrevet i
 * ≥6 hooks. Ett glemt soft-delete-filter = feil tall. Denne modulen er den ene
 * autoritative kilden for hvordan billing-input hentes.
 *
 * Supabase importeres her (lib/), ikke i hooks utover dette — hooks kaller
 * fetchBillingInputs og sender resultatet videre til calcBillingRatesData.
 */
import { supabase } from "./supabase";
import { fetchAllPaginated } from "./supabase-paginate";
import type { BillingRatesInput } from "./billing-rates-calc";

/** Authoritative select for fakturerbare assignments (Erik-lock 2026-06-02).
 *  VIB-994: joiner upsells(billing_mode) så T&M-mersalg-linjer kan ekskluderes
 *  fra tildelte-timer-pathen når feature_tm_upsell er på.
 *
 *  FK-hintet er påkrevd: budget_lines og upsells har to fremmednøkler mellom
 *  seg (budget_lines.upsell_id, og den eldre upsells.budget_line_id fra før
 *  VIB-552). Uten hint svarer PostgREST PGRST201 (HTTP 300). Vi vil ha
 *  mersalget linja tilhører, altså budget_lines.upsell_id. */
export const UPSELL_OF_BUDGET_LINE = "upsells!budget_lines_upsell_id_fkey";

export const billableAssignmentsSelect =
  "id, user_id, hours, task_status, budget_line_id, completed_at, approved_at, " +
  `budget_lines!inner(project_id, cost_type, quantity_hours, ${UPSELL_OF_BUDGET_LINE}(billing_mode), ` +
  "projects!inner(deleted_at, project_status, project_type))";

/**
 * Select for per-oppgave-grunnlaget (useBillingRateBreakdown). Samme
 * inner-join + soft-delete/cancelled-felter som {@link billableAssignmentsSelect},
 * men med ekstra visningsfelter (description, project_code, name).
 */
export const breakdownAssignmentsSelect =
  "hours, task_status, budget_line_id, completed_at, approved_at, " +
  "budget_lines!inner(description, cost_type, quantity_hours, " +
  "projects!inner(project_code, name, deleted_at, project_status))";

export interface BillingInputs {
  users: BillingRatesInput["users"];
  billableAssignments: BillingRatesInput["billableAssignments"];
  timeEntries: BillingRatesInput["timeEntries"];
  absences: BillingRatesInput["absences"];
  confirmations: BillingRatesInput["confirmations"];
  reisetidEntries: NonNullable<BillingRatesInput["reisetidEntries"]>;
  hourlyEntries: NonNullable<BillingRatesInput["hourlyEntries"]>;
  tmUpsellEntries: NonNullable<BillingRatesInput["tmUpsellEntries"]>;
  tmEnabled: boolean;
  holidays: string[];
}

/**
 * Henter all input til faktureringsgrad-beregningen for ett år.
 * `userId` undefined = hele teamet; satt = kun den brukeren.
 *
 * Soft-delete-/cancelled-filteret påføres ETT sted her — det er hele poenget
 * med konsolideringen.
 *
 * `includeBillable=false` hopper over assignment- og reisetid-pagineringen.
 * Brukes av use-billing-rates der billable kommer ferdig fra get_billing_rates-
 * RPC-en (TOPP-4) — da trenger vi bare capacity-input (users/fravær/helligdager/
 * bekreftelser/loggede timer).
 */
export async function fetchBillingInputs(params: {
  year: number;
  userId?: string;
  includeBillable?: boolean;
}): Promise<BillingInputs> {
  const { year, userId, includeBillable = true } = params;
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const nextYearStart = `${year + 1}-01-01`;

  // Users
  let usersQuery = supabase
    .from("users")
    .select("id, full_name, avatar_url, monthly_capacity_hours, target_billing_rate");
  usersQuery = userId
    ? usersQuery.eq("id", userId)
    : usersQuery.eq("is_active", true).order("full_name");
  const { data: users, error: usersError } = await usersQuery;
  if (usersError) throw usersError;

  // Billable assignments (paginert; soft-delete/cancelled-filter)
  const billableAssignments = includeBillable
    ? await fetchAllPaginated<BillingRatesInput["billableAssignments"][number]>(
        "budget_line_assignments",
        billableAssignmentsSelect,
        (q) => {
          let qq = q
            .in("task_status", ["completed", "approved"])
            .is("budget_lines.projects.deleted_at", null)
            .neq("budget_lines.projects.project_status", "cancelled");
          if (userId) qq = qq.eq("user_id", userId);
          return qq;
        },
      )
    : [];

  // Time entries (paginert)
  const timeEntries = await fetchAllPaginated<
    BillingRatesInput["timeEntries"][number] & { project_id: string | null }
  >(
    "time_entries",
    "user_id, duration_seconds, start_date, budget_line_id, project_id",
    (q) => {
      let qq = q
        .is("deleted_at", null)
        .gte("start_date", yearStart)
        .lt("start_date", nextYearStart);
      if (userId) qq = qq.eq("user_id", userId);
      return qq;
    },
  );

  // Absences: kapasitet (dato og timer) via absence_capacity, som også gir
  // team-tall til roller som ikke får lese fraværstype (VIB-2915).
  const { data: capacityRows, error: absErr } = await supabase.rpc("absence_capacity", {
    p_from: yearStart,
    p_to: yearEnd,
  });
  if (absErr) throw absErr;
  const absences = userId
    ? (capacityRows ?? []).filter((a: { user_id: string }) => a.user_id === userId)
    : capacityRows;

  // Confirmations
  let confQuery = supabase
    .from("monthly_confirmations")
    .select("*")
    .eq("year", year);
  if (userId) confQuery = confQuery.eq("user_id", userId);
  const { data: confirmations } = await confQuery;

  // Holidays (global)
  const { data: holidayRows } = await supabase
    .from("company_holidays")
    .select("date")
    .gte("date", yearStart)
    .lt("date", nextYearStart);

  // Reisetid (VIB-909): loggede timer 1:1 på reisetid-malen
  const { data: rt } = includeBillable
    ? await supabase
        .from("offer_line_templates")
        .select("id")
        .eq("is_medgatt", true)
        .eq("name", "Reisetid (medgått)")
        .maybeSingle()
    : { data: null };

  let reisetidEntries: NonNullable<BillingRatesInput["reisetidEntries"]> = [];
  if (rt?.id) {
    const rtData = await fetchAllPaginated<{
      user_id: string;
      duration_seconds: number | null;
      start_date: string;
    }>(
      "time_entries",
      "user_id, duration_seconds, start_date, budget_lines!inner(template_id, projects!inner(deleted_at, project_status))",
      (q) => {
        let qq = q
          .is("deleted_at", null)
          .gte("start_date", yearStart)
          .lt("start_date", nextYearStart)
          .eq("budget_lines.template_id", rt.id)
          .is("budget_lines.projects.deleted_at", null)
          .neq("budget_lines.projects.project_status", "cancelled");
        if (userId) qq = qq.eq("user_id", userId);
        return qq;
      },
    );
    reisetidEntries = rtData.map((e) => ({
      user_id: e.user_id,
      duration_seconds: e.duration_seconds,
      start_date: e.start_date,
    }));
  }

  // Hourly / T&M (VIB-923): billable = loggede timer på hourly-prosjekter
  const { data: hourlyProjects } = includeBillable
    ? await supabase
        .from("projects")
        .select("id")
        .eq("project_type", "hourly")
        .is("deleted_at", null)
        .neq("project_status", "cancelled")
    : { data: [] };
  const hourlyIds = new Set((hourlyProjects ?? []).map((p: { id: string }) => p.id));
  const hourlyEntries = includeBillable
    ? (timeEntries ?? [])
        .filter((te) => te.project_id && hourlyIds.has(te.project_id))
        .map((te) => ({
          user_id: te.user_id,
          duration_seconds: te.duration_seconds,
          start_date: te.start_date,
        }))
    : [];

  // VIB-994: T&M-mersalg (feature_tm_upsell). Les flagg-nivået; av (default) →
  // T&M-grenen er no-op og fetchen hoppes over.
  let tmEnabled = false;
  if (includeBillable) {
    const { data: flagRow } = await supabase
      .from("system_config")
      .select("value")
      .eq("key", "feature_tm_upsell")
      .maybeSingle();
    const level = flagRow?.value;
    tmEnabled = typeof level === "string" && level !== "off";
  }

  // Loggede timer på T&M-mersalg-linjer (upsell.billing_mode='tm', approved) —
  // krediteres 1:1. Kun når flagget er på.
  let tmUpsellEntries: NonNullable<BillingRatesInput["tmUpsellEntries"]> = [];
  if (includeBillable && tmEnabled) {
    const tmData = await fetchAllPaginated<{
      user_id: string;
      duration_seconds: number | null;
      start_date: string;
    }>(
      "time_entries",
      "user_id, duration_seconds, start_date, " +
        `budget_lines!inner(${UPSELL_OF_BUDGET_LINE}!inner(billing_mode, status), projects!inner(deleted_at, project_status))`,
      (q) => {
        let qq = q
          .is("deleted_at", null)
          .gte("start_date", yearStart)
          .lt("start_date", nextYearStart)
          .eq("budget_lines.upsells.billing_mode", "tm")
          .eq("budget_lines.upsells.status", "approved")
          .is("budget_lines.projects.deleted_at", null)
          .neq("budget_lines.projects.project_status", "cancelled");
        if (userId) qq = qq.eq("user_id", userId);
        return qq;
      },
    );
    tmUpsellEntries = tmData.map((e) => ({
      user_id: e.user_id,
      duration_seconds: e.duration_seconds,
      start_date: e.start_date,
    }));
  }

  return {
    users: (users ?? []) as BillingRatesInput["users"],
    billableAssignments: (billableAssignments ?? []) as unknown as BillingRatesInput["billableAssignments"],
    timeEntries: (timeEntries ?? []) as unknown as BillingRatesInput["timeEntries"],
    absences: (absences ?? []) as BillingRatesInput["absences"],
    confirmations: (confirmations ?? []) as unknown as BillingRatesInput["confirmations"],
    reisetidEntries,
    hourlyEntries,
    tmUpsellEntries,
    tmEnabled,
    holidays: (holidayRows ?? []).map((h: { date: string }) => h.date),
  };
}
